import { Select, Tabs, Row, Col } from "antd";
import { ShieldCheck, Users } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useCommunitySelector } from "hooks/useCommunitySelector/useCommunitySelector";
import TiersTab from "./_blocks/TiersTab";
import MembersTab from "./_blocks/MembersTab";

const TAB_ITEMS = (communityId) => [
  {
    key: "tiers",
    label: (
      <span className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4" /> Tiers & Plans
      </span>
    ),
    children: <TiersTab communityId={communityId} />,
  },
  {
    key: "members",
    label: (
      <span className="flex items-center gap-2">
        <Users className="h-4 w-4" /> Members
      </span>
    ),
    children: <MembersTab communityId={communityId} />,
  },
];

function Memberships() {
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    communityOptions,
    selectedCommunityId,
    setSelectedCommunityId,
    selectedCommunity,
    loading,
  } = useCommunitySelector({
    initialCommunityId: searchParams.get("community_id"),
  });

  const handleCommunityChange = (val) => {
    setSelectedCommunityId(val ?? null);
    if (val) {
      setSearchParams({ community_id: val });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Memberships</h1>
          <p className="text-muted-foreground text-sm">
            Manage tiered subscription plans and members for your community.
          </p>
        </div>
        <div className="shrink-0">
          <Row gutter={8} align="middle" wrap={false}>
            <Col>
              <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                Community:
              </span>
            </Col>
            <Col>
              <Select
                style={{ minWidth: 220 }}
                placeholder="Select a community"
                options={communityOptions}
                value={selectedCommunityId}
                onChange={handleCommunityChange}
                loading={loading}
                showSearch
                optionFilterProp="label"
                allowClear
              />
            </Col>
          </Row>
        </div>
      </div>

      {/* Gate: nothing renders until a community is chosen */}
      {!selectedCommunityId ? (
        <div className="flex flex-col items-center justify-center py-28 text-center space-y-4 bg-card rounded-2xl border border-dashed">
          <div className="h-16 w-16 rounded-full bg-gradient-to-br from-lavender-100 to-lavender-50 flex items-center justify-center">
            <ShieldCheck className="h-8 w-8 text-muted-foreground" />
          </div>
          <div className="space-y-1">
            <h3 className="font-bold">Select a community to get started</h3>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto">
              Choose a community from the dropdown above to manage its
              membership tiers, pricing plans, and members.
            </p>
          </div>
          {selectedCommunity && (
            <p className="text-sm font-semibold text-primary">
              {selectedCommunity.name}
            </p>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-border/50 bg-card overflow-hidden shadow-card">
          <div className="px-4 md:px-6">
            <Tabs
              defaultActiveKey="tiers"
              items={TAB_ITEMS(selectedCommunityId)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Memberships;
